import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Select, MenuItem, Button, FormControl, InputLabel,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TableSortLabel, TextField, Chip, Alert, CircularProgress, Paper,
  Grid, Card, CardContent,
} from '@mui/material';
import { Download as DownloadIcon, Save as SaveIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { fetchHistoryDates, fetchHistoryPreview, saveSnapshot, getDownloadUrl } from '../api/history';
import type { HistoryDateInfo, HistoryStockData } from '../types';
import { getPriceColor, formatPct, formatVolume, formatAmount } from '../utils/format';

const SORT_COLUMNS = [
  { key: 'turnover_rate', label: '换手率' },
  { key: 'change_pct', label: '涨跌幅' },
  { key: 'volume', label: '成交量' },
  { key: 'amount', label: '成交额' },
  { key: 'amplitude', label: '振幅' },
  { key: 'close', label: '收盘价' },
];

export default function HistoryPage() {
  const [dates, setDates] = useState<HistoryDateInfo[]>([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [stocks, setStocks] = useState<HistoryStockData[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [size] = useState(50);
  const [sort, setSort] = useState('turnover_rate');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);

  const loadDates = useCallback(async () => {
    try {
      const data = await fetchHistoryDates();
      setDates(data);
      if (data.length > 0 && !selectedDate) {
        setSelectedDate(data[0].date);
      }
    } catch (e: any) {
      setMessage({ type: 'error', text: e.message || '获取日期列表失败' });
    }
  }, [selectedDate]);

  useEffect(() => {
    loadDates();
  }, [loadDates]);

  const loadData = useCallback(async () => {
    if (!selectedDate) return;
    setLoading(true);
    try {
      const result = await fetchHistoryPreview({
        date: selectedDate, page, size, sort, order, search,
      });
      setStocks(result.data);
      setTotal(result.total);
    } catch (e: any) {
      setMessage({ type: 'error', text: e.message || '获取数据失败' });
      setStocks([]);
    } finally {
      setLoading(false);
    }
  }, [selectedDate, page, size, sort, order, search]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSort = (col: string) => {
    if (col === sort) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setSort(col);
      setOrder('desc');
    }
    setPage(1);
  };

  const handleSaveSnapshot = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const result = await saveSnapshot();
      setMessage({ type: 'success', text: `${result.date} 快照保存成功：${result.saved} 只股票` });
      await loadDates();
      setSelectedDate(result.date);
    } catch (e: any) {
      setMessage({ type: 'error', text: e.message || '保存快照失败' });
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = () => {
    if (!selectedDate) return;
    window.open(getDownloadUrl(selectedDate), '_blank');
  };

  const totalPages = Math.max(1, Math.ceil(total / size));

  // Stats for the selected date
  const stats = React.useMemo(() => {
    if (!dates.length || !selectedDate) return null;
    const info = dates.find((d) => d.date === selectedDate);
    return info;
  }, [dates, selectedDate]);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          历史数据
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<SaveIcon />}
            onClick={handleSaveSnapshot}
            disabled={saving}
          >
            {saving ? '保存中...' : '保存今日快照'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={loadDates}
          >
            刷新
          </Button>
        </Box>
      </Box>

      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      {/* Date selector + Download */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>选择日期</InputLabel>
              <Select
                value={selectedDate}
                label="选择日期"
                onChange={(e) => { setSelectedDate(e.target.value); setPage(1); }}
              >
                {dates.map((d) => (
                  <MenuItem key={d.date} value={d.date}>
                    {d.date} ({d.count} 只)
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              disabled={!selectedDate}
            >
              下载 CSV
            </Button>
          </Grid>
          <Grid item xs={12} sm={5}>
            {stats && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip size="small" label={`日期 ${stats.date}`} color="primary" variant="outlined" />
                <Chip size="small" label={`股票数 ${stats.count}`} color="secondary" variant="outlined" />
              </Box>
            )}
          </Grid>
        </Grid>
      </Paper>

      {/* Search */}
      <Box sx={{ mb: 1 }}>
        <TextField
          size="small"
          placeholder="搜索代码或名称..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          sx={{ width: 300 }}
        />
      </Box>

      {/* Data table */}
      <TableContainer component={Paper} sx={{ maxHeight: 600, overflow: 'auto' }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell>代码</TableCell>
              <TableCell>名称</TableCell>
              {SORT_COLUMNS.map((col) => (
                <TableCell key={col.key} align="right" sortDirection={sort === col.key ? order : false}>
                  <TableSortLabel
                    active={sort === col.key}
                    direction={sort === col.key ? order : 'desc'}
                    onClick={() => handleSort(col.key)}
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell align="center">涨停</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                <CircularProgress size={24} />
              </TableCell>
            </TableRow>
          ) : stocks.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                {selectedDate ? `${selectedDate} 暂无数据` : '请选择日期'}
              </TableCell>
            </TableRow>
          ) : (
            stocks.map((s) => (
              <TableRow key={s.code} hover>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 13 }}>{s.code}</TableCell>
                <TableCell sx={{ fontSize: 13 }}>{s.name}</TableCell>
                <TableCell align="right" sx={{ fontSize: 13 }}>{s.close?.toFixed(2) ?? '--'}</TableCell>
                <TableCell align="right" sx={{ fontSize: 13, color: getPriceColor(s.changePct), fontWeight: 600 }}>
                  {formatPct(s.changePct)}
                </TableCell>
                <TableCell align="right" sx={{ fontSize: 13 }}>{formatVolume(s.volume)}</TableCell>
                <TableCell align="right" sx={{ fontSize: 13 }}>{formatAmount(s.amount)}</TableCell>
                <TableCell align="right" sx={{ fontSize: 13 }}>{s.amplitude?.toFixed(2) ?? '--'}%</TableCell>
                <TableCell align="right" sx={{ fontSize: 13, color: s.turnoverRate >= 7 ? 'error.main' : 'text.primary', fontWeight: s.turnoverRate >= 7 ? 700 : 400 }}>
                  {s.turnoverRate?.toFixed(2) ?? '--'}%
                </TableCell>
                <TableCell align="center">
                  {s.isLimitUp ? <Chip size="small" label="涨停" color="error" sx={{ height: 20, fontSize: 11 }} /> : ''}
                </TableCell>
              </TableRow>
            ))
          )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 1, gap: 2 }}>
        <Button size="small" disabled={page <= 1} onClick={() => setPage(page - 1)}>上一页</Button>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          第 {page} 页 / 共 {totalPages} 页 ({total} 只)
        </Typography>
        <Button size="small" disabled={page * size >= total} onClick={() => setPage(page + 1)}>下一页</Button>
      </Box>

      {/* Info card */}
      <Card sx={{ mt: 2, bgcolor: 'info.main', color: 'info.contrastText' }}>
        <CardContent>
          <Typography variant="body2">
            每个交易日 15:05 自动保存全市场行情快照。也可手动点击"保存今日快照"立即保存当前行情。
            下载的 CSV 文件可用 Excel 打开，包含代码、名称、开/收/高/低、成交量、成交额、振幅、涨跌幅、换手率、涨停标记。
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}
